import PropTypes from 'prop-types';

import React from 'react';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Map, List } from 'immutable';

import { DragSource, DropTarget } from 'react-dnd';
import flow from 'lodash/flow';

import { exportBlock } from '../../utils/exportContent';
import { loadBlockSchema } from '../../utils/schema';

import {
  selectEditorReadOnly,
  selectActiveBlockKey,
  selectNewBlockKey,
  selectEditorBlocksCount,
} from '../EditorContainer/selectors';

import {
  deleteMedia,
  moveBlock,
  deleteBlock,
  setBlockActive,
  setBlockNotNew,
  mergeBlockData,
  changeBlockType,
} from '../EditorContainer/actions';

import {
  uploadMediaItems,
  uploadMediaItemsFailed,
} from '../../api/medialib/actions';

import Block from '../../components/Block';
import EditorAddContainer from '../EditorAddContainer';

export class BlockContainer extends React.PureComponent {
  constructor(props) {
    super(props);

    this._handlers = {
      onChangeBlockField: this.handleChangeBlockField,
      onChangeBlockType: this.handleChangeBlockType,
      onDropFile: this.handleDropFile,
      onFocusBlock: this.handleFocusBlock,
      onAddNewInput: this.handleAddNewInput,
      onRemove: this.handleRemove,
      onRemoveMedia: this.handleRemoveMedia,
    };

    this.handleChangeBlockField = this._buildHandler('onChangeBlockField');
    this.handleChangeBlockType = this._buildHandler('onChangeBlockType');
    this.handleDropFile = this._buildHandler('onDropFile');
    this.handleFocusBlock = this._buildHandler('onFocusBlock');
    this.handleAddNewInput = this._buildHandler('onAddNewInput');
    this.handleRemove = this._buildHandler('onRemove');
    this.handleRemoveMedia = this._buildHandler('onRemoveMedia');

    this.initSchema();
  }

  getChildContext() {
    return {
      blockIndex: this.props.index,
      onAddNewInput: this.handleAddNewInput,
      onFocusBlock: this.handleFocusBlock,
    };
  }

  componentDidMount() {
    this.validate();
  }

  componentDidUpdate(prevProps) {
    if (this.props.block.get('type') !== prevProps.block.get('type')) {
      this.initSchema();
    }

    if (this.props.block !== prevProps.block) {
      this.validate();
    }
  }

  get blockKey() {
    return this.props.block.get('key');
  }

  get blockType() {
    return this.props.block.get('type');
  }

  handleChangeBlockField = (value, field = 'value') => {
    this.props.mergeBlockData(this.blockKey, { [field]: value });
  }

  handleChangeBlockType = blockType => {
    this.props.changeBlockType(this.blockKey, blockType);
  }

  handleDropFile = (acceptedFiles, rejectedFiles) => {
    if (acceptedFiles && acceptedFiles.length) {
      this.props.uploadMediaItems(this.blockKey, acceptedFiles);
    } else if (rejectedFiles && rejectedFiles.length) {
      this.props.uploadMediaItemsFailed(this.blockKey, {
        0: 'Неподходящий тип или размер файла',
      });
    }
  }

  handleFocusBlock = () => {
    this.props.setBlockActive(this.blockKey);
  }

  handleAddNewInput = (inputRef) => {
    if (this.blockKey === this.props.newBlockKey) {
      this.inputRef = inputRef;
      this.props.onAddNewInput(inputRef);
      this.props.setBlockNotNew(this.blockKey);
    }
  }

  handleRemove = () => this.props.deleteBlock(this.blockKey);

  handleRemoveMedia = () => this.props.deleteMedia(this.blockKey);

  handleIndexChange = (newIndex) => {
    if (newIndex >= 0) {
      this.props.moveBlock(this.props.index, newIndex);
    }
  }

  validate({ block } = this.props) {
    if (!this.ajv) {
      return;
    }

    const isValid = this.ajv.validate(this.blockType, exportBlock(block).toJS());

    if (block.get('isValid') !== isValid) {
      this.props.mergeBlockData(this.blockKey, { isValid });
    }
  }

  _buildHandler = eventName => (...args) => {
    if (!this.props.readOnly) {
      const method = this._handlers[eventName];
      try {
        if (typeof method === 'function') {
          method(...args);
        } else {
          throw new Error(`there's no handler for '${eventName}' event`);
        }
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    }
  }

  initSchema = () => {
    if (this.blockType.startsWith('_')) {
      delete this.ajv;
      return;
    }

    this.ajv = loadBlockSchema(this.blockType);
  }

  render() {
    const {
      block,
      index,
      buttons,
      children,
      readOnly,
      activeBlockKey,

      connectDragPreview,
      connectDragSource,
      connectDropTarget,
    } = this.props;

    return connectDragPreview(connectDropTarget(
      <div>
        <Block
          block={block}
          type={this.blockType}
          index={index}
          maxIndex={this.props.blocksCount}
          buttons={buttons}
          active={this.blockKey === activeBlockKey}
          readOnly={readOnly}
          postType={this.props.postType}

          onChangeBlockField={this.handleChangeBlockField}
          onChangeBlockType={this.handleChangeBlockType}
          onDropFile={this.handleDropFile}
          onAddNewInput={this.handleAddNewInput}
          onRemove={this.handleRemove}
          onRemoveMedia={this.handleRemoveMedia}
          onIndexChange={this.handleIndexChange}

          connectDragSource={connectDragSource}
        >
          {children}
        </Block>

        <EditorAddContainer index={index + 1} readOnly={readOnly} />
      </div>
    ));
  }
}

const blockSource = {
  beginDrag(props) {
    return {
      key: props.block.get('key'),
      index: props.index,
    };
  },
};

const blockTarget = {
  hover(props, monitor, component) {
    if (props.readOnly) {
      return;
    }

    const dragIndex = monitor.getItem().index;
    const hoverIndex = props.index;

    // Don't replace items with themselves
    if (dragIndex === hoverIndex) {
      return;
    }

    // Determine rectangle on screen
    const hoverBoundingRect = findDOMNode(component).getBoundingClientRect(); // eslint-disable-line react/no-find-dom-node

    // Get vertical middle
    const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

    // Determine mouse position
    const clientOffset = monitor.getClientOffset();

    // Get pixels to the top
    const hoverClientY = clientOffset.y - hoverBoundingRect.top;

    // Only perform the move when the mouse has crossed half of the items height
    // When dragging downwards, only move when the cursor is below 50%
    // When dragging upwards, only move when the cursor is above 50%

    // Dragging downwards
    if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
      return;
    }

    // Dragging upwards
    if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
      return;
    }

    // Time to actually perform the action
    props.moveBlock(dragIndex, hoverIndex);

    // Note: we're mutating the monitor item here!
    // Generally it's better to avoid mutations,
    // but it's good here for the sake of performance
    // to avoid expensive index searches.
    monitor.getItem().index = hoverIndex; // eslint-disable-line no-param-reassign
  },
};

BlockContainer.propTypes = {
  block: PropTypes.instanceOf(Map).isRequired,
  index: PropTypes.number,
  blocksCount: PropTypes.number,
  buttons: PropTypes.instanceOf(List),
  children: PropTypes.node,
  readOnly: PropTypes.bool,
  activeBlockKey: PropTypes.string,
  newBlockKey: PropTypes.string,
  postType: PropTypes.oneOf(['short', 'longread']),

  connectDragSource: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,

  onAddNewInput: PropTypes.func,

  uploadMediaItems: PropTypes.func,
  uploadMediaItemsFailed: PropTypes.func,
  deleteMedia: PropTypes.func,
  moveBlock: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
  deleteBlock: PropTypes.func,
  setBlockActive: PropTypes.func,
  setBlockNotNew: PropTypes.func,
  mergeBlockData: PropTypes.func,
  changeBlockType: PropTypes.func,
};

BlockContainer.defaultProps = {
  buttons: List(),
  postType: undefined,
};

BlockContainer.childContextTypes = {
  blockIndex: PropTypes.number,
  onAddNewInput: PropTypes.func,
  onFocusBlock: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  readOnly: selectEditorReadOnly(),
  activeBlockKey: selectActiveBlockKey(),
  newBlockKey: selectNewBlockKey(),
  blocksCount: selectEditorBlocksCount(),
});

export function mapDispatchToProps(dispatch) {
  return {
    uploadMediaItems: (blockKey, files) => {
      dispatch(uploadMediaItems(blockKey, files));
    },
    uploadMediaItemsFailed: (blockKey, errors) => {
      dispatch(uploadMediaItemsFailed(blockKey, errors));
    },
    deleteMedia: (blockKey) => {
      dispatch(deleteMedia(blockKey));
    },
    moveBlock: (currentPosition, newPosition) => {
      dispatch(moveBlock(currentPosition, newPosition));
    },
    deleteBlock: (blockKey) => {
      dispatch(deleteBlock(blockKey));
    },
    setBlockActive: (blockKey) => {
      dispatch(setBlockActive(blockKey));
    },
    setBlockNotNew: (blockKey) => {
      dispatch(setBlockNotNew(blockKey));
    },
    mergeBlockData: (blockKey, data) => {
      dispatch(mergeBlockData(blockKey, data));
    },
    changeBlockType: (blockKey, blockType) => {
      dispatch(changeBlockType(blockKey, blockType));
    },
  };
}

export default flow(
  DragSource(props => props.activeBlockKey, blockSource, connector => ({
    connectDragSource: connector.dragSource(),
    connectDragPreview: connector.dragPreview(),
  })),
  DropTarget(props => props.activeBlockKey, blockTarget, connector => ({
    connectDropTarget: connector.dropTarget(),
  })),
  connect(mapStateToProps, mapDispatchToProps)
)(BlockContainer);
